import React, {useCallback, useContext} from 'react';

import {AccountTeamMemberSettings} from 'src/admin-rights-service/modules/admin-profile/sections/account-team-member-settings';
import {AdminRoleSettings} from 'src/admin-rights-service/modules/admin-profile/sections/admin-role-settings';
import {ProfileMain} from 'src/components/profile-tab-structure';
import {SaveWithReasonButton} from 'src/components/save-with-reason/save-with-reason-button';
import {DISCARD_CHANGES_SUCCESS_MESSAGE, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {MessageContext} from 'src/contexts/message-context';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {ErrorPage} from 'src/modules/error-page/error-page.components';
import {UserRoleSettingsController} from 'src/modules/profile/account/create-user/steps/user-role-settings';

import Button from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import Typography from '@pb/kit-component-typography';

import {ADMIN_ROLE_SETTINGS_TABS} from './constants';
import {UserRoleSettingsTabs} from './user-role-settings-tabs';

import type {MessageContextType} from 'src/contexts/message-context';


type Props = {
    userId: number,
    accountId: number,
};

export const UserRoleSettings = ({userId, accountId}: Props) => {
    const {setSuccessMessage} = useContext<MessageContextType>(MessageContext);
    const {canManageUserRole} = useAdminAccessRight();
    const {userSubmit, userRoleUpdate} = useEventTracking();

    const onProceed = useCallback(() => {
        setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
        userSubmit({place: 'Role Settings', submit: true});
    }, [setSuccessMessage, userSubmit]);

    return (
        <ValidationContextProvider>
            <UserRoleSettingsTabs userId={userId} activeSubTab={ADMIN_ROLE_SETTINGS_TABS.SETTINGS.name}/>
            <UserRoleSettingsController userId={userId} onProceed={onProceed}>
                {({
                    busy,
                    isLoading,
                    loadingError,
                    isChunkChanged,

                    roles,
                    role, changeRole,
                    defaultAdminPermissions,
                    adminPermissions, setAdminPermissions,
                    accountTeamMemberSettings, setAccountTeamMemberSettings,

                    isDefaultAdminPermissions,
                    saveChanges,
                    discardChanges,
                    resetAdminPermissions,
                }) => {
                    if (loadingError?.status === 403) {
                        return <ErrorPage title="Access Denied" subtitle="You have no access to this page"/>;
                    } else if (loadingError) {
                        return <ErrorPage subtitle="Please try again later." title="Something went wrong."/>;
                    }

                    return (
                        <ProfileMain header={(
                            <Item grow={1}>
                                <Group justify="space-between">
                                    <Typography
                                        fontWeight="semi-bold"
                                        variant="heading-level-3">
                                        User Role Settings
                                    </Typography>
                                    {canManageUserRole(accountId) ? (
                                        <Group>
                                            <Button
                                                variant="Type.SECONDARY"
                                                onClick={() => {
                                                    discardChanges();
                                                    userSubmit({place: 'Role Settings', submit: false});
                                                    setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
                                                }}
                                                disabled={!isChunkChanged || isLoading}>
                                                Discard Changes
                                            </Button>
                                            <SaveWithReasonButton
                                                busy={busy}
                                                disabled={!isChunkChanged || isLoading}
                                                onSave={saveChanges}
                                            />
                                        </Group>
                                    ) : null}
                                </Group>
                            </Item>
                        )}>
                            <AccountTeamMemberSettings {...{
                                disabled: !canManageUserRole(accountId),
                                isLoading,
                                accountTeamMemberSettings,
                                setAccountTeamMemberSettings: data => {
                                    userRoleUpdate({
                                        parameterName: 'Account Team Member',
                                        targetUserId: role?.profileId ?? -1,
                                    });
                                    return setAccountTeamMemberSettings(data);
                                },
                            }}
                            />
                            <AdminRoleSettings {...{
                                disabled: !canManageUserRole(accountId),
                                isLoading,
                                role,
                                roles,
                                isDefaultAdminPermissions,
                                resetAdminPermissions,
                                changeRole: data => {
                                    userRoleUpdate({
                                        parameterName: 'Admin Role',
                                        targetUserId: role?.profileId ?? -1,
                                    });
                                    return changeRole(data);
                                },
                                adminPermissions,
                                defaultAdminPermissions,
                                setAdminPermissions: data => {
                                    userRoleUpdate({
                                        parameterName: 'Admin Rights',
                                        targetUserId: role?.profileId ?? -1,
                                    });
                                    return setAdminPermissions(data);
                                },
                            }}
                            />
                        </ProfileMain>
                    );
                }}
            </UserRoleSettingsController>
        </ValidationContextProvider>
    );
};
